import { addDays } from "./dates";
import {
  avgTicket,
  defaultDashboardPeriod,
  dreValues,
  lastDayOfMonth,
  previousMonth,
  sumOrNull,
} from "./dashboard";
import { formatBRL } from "./money";
import { formatPct } from "./pct";
import { formatQty } from "./qty";

/**
 * Copiloto somente leitura (T1.14, 06-ia-agentes): períodos e métricas do `query_metrics`, cotas diárias, limpeza da
 * resposta do modelo e o número do WhatsApp (RF-BI-13). Sem I/O.
 */

// ---------- métricas ----------

export const COPILOT_METRICS = [
  "revenue",
  "net_revenue",
  "orders",
  "items",
  "avg_ticket",
  "cost",
  "gross_profit",
  "gross_margin",
  "profit",
] as const;
export type CopilotMetric = (typeof COPILOT_METRICS)[number];
/** Métricas que revelam o custo: só com products:read_cost. */
export const COPILOT_COST_METRICS: readonly CopilotMetric[] = [
  "cost",
  "gross_profit",
  "gross_margin",
  "profit",
];
/** Por produto não há frete nem pedido inteiro: estas não existem no agrupamento por produto. */
export const PRODUCT_GROUP_METRICS: readonly CopilotMetric[] = [
  "revenue",
  "orders",
  "items",
  "cost",
  "gross_profit",
  "gross_margin",
];

export const METRICS_PERIODS = [
  "today",
  "yesterday",
  "last_7_days",
  "last_30_days",
  "this_month",
  "last_month",
  "this_year",
  "last_year",
] as const;
export type MetricsPeriod = (typeof METRICS_PERIODS)[number];
export const METRICS_GROUPS = [
  "none",
  "day",
  "month",
  "channel",
  "seller",
  "uf",
  "status",
  "product",
] as const;
export type MetricsGroup = (typeof METRICS_GROUPS)[number];

/** Período do `query_metrics`: datas explícitas (já validadas pelo contrato) ou atalho; padrão = mês até hoje. */
export function resolveMetricsPeriod(
  q: { period?: MetricsPeriod; from?: string; to?: string },
  today: string,
): { from: string; to: string } {
  if (q.from && q.to) return { from: q.from, to: q.to };
  const year = today.slice(0, 4);
  switch (q.period ?? "this_month") {
    case "today":
      return { from: today, to: today };
    case "yesterday": {
      const d = addDays(today, -1);
      return { from: d, to: d };
    }
    case "last_7_days":
      return { from: addDays(today, -6), to: today };
    case "last_30_days":
      return { from: addDays(today, -29), to: today };
    case "this_month":
      return defaultDashboardPeriod(today);
    case "last_month": {
      const m = previousMonth(today.slice(0, 7));
      return { from: `${m}-01`, to: lastDayOfMonth(m) };
    }
    case "this_year":
      return { from: `${year}-01-01`, to: today };
    case "last_year": {
      const y = String(Number(year) - 1).padStart(4, "0");
      return { from: `${y}-01-01`, to: `${y}-12-31` };
    }
  }
}

export type MetricSums = {
  totalCents: number | null;
  freightCents: number | null;
  orders: number | null;
  qtyQ4: number | null;
  costCents: number | null;
};

/** Valor da métrica (centavos, contagem, q4 ou pct4) a partir das somas; null quando desconhecido ou fora do seguro. */
export function metricValue(
  m: CopilotMetric,
  s: MetricSums,
  cfg: { taxPct4: number; feePct4: number },
): number | null {
  const dre = () =>
    dreValues({
      totalCents: s.totalCents,
      freightCents: s.freightCents,
      costCents: s.costCents,
      taxPct4: cfg.taxPct4,
      feePct4: cfg.feePct4,
    });
  switch (m) {
    case "revenue":
      return s.totalCents;
    case "net_revenue":
      return dre().netRevenueCents;
    case "orders":
      return s.orders;
    case "items":
      return s.qtyQ4;
    case "avg_ticket":
      return avgTicket(s.totalCents, s.orders);
    case "cost":
      return s.costCents;
    case "gross_profit": {
      const net = dre().netRevenueCents;
      return net === null || s.costCents === null ? null : sumOrNull([net, -s.costCents]);
    }
    case "gross_margin":
      return dre().grossMarginPct4;
    case "profit":
      return dre().profitCents;
  }
}

/** Texto pt-BR da métrica: "R$ 1.234,56", "12", "3,5" (peças) ou "12,35%"; desconhecido = "—". */
export function formatMetric(m: CopilotMetric, v: number | null): string {
  if (v === null) return "—";
  if (m === "orders") return String(v);
  if (m === "items") return formatQty(v, 0);
  if (m === "gross_margin") return formatPct(Math.round(v / 100) * 100);
  return formatBRL(v);
}

// ---------- cotas ----------

export type CopilotCaps = {
  userRequests: number;
  userTokens: number;
  companyRequests: number;
  companyTokens: number;
};
/** Cotas diárias padrão (America/Sao_Paulo); pergunta ao PO: ligar ao plano (`plans.limits.ai_credits`). */
export const COPILOT_DEFAULT_CAPS: CopilotCaps = {
  userRequests: 100,
  userTokens: 300_000,
  companyRequests: 1_000,
  companyTokens: 3_000_000,
};
export type CopilotUsage = {
  userRequests: number;
  userTokens: number;
  companyRequests: number;
  companyTokens: number;
};

const USER_OVER = "Você chegou ao limite diário do Copiloto. Tente de novo amanhã.";
const COMPANY_OVER = "A empresa chegou ao limite diário do Copiloto. Tente de novo amanhã.";

/**
 * Tokens reservados no começo de cada pergunta (revisão de segurança T1.14): a cota de tokens só era conferida no
 * começo, e perguntas em paralelo ou um laço longo passavam do limite. A reserva entra no uso do dia em `begin` e é
 * acertada pelo gasto real no fim (o excedente é cobrado e conferido antes de cada chamada ao modelo).
 */
export const COPILOT_TOKEN_RESERVE = 12_000;
/** Perguntas em andamento ao mesmo tempo por usuário (web + WhatsApp). */
export const COPILOT_MAX_INFLIGHT = 2;

/** Mensagem da cota estourada no começo da pergunta (null = pode perguntar); `reserve` precisa caber na cota. */
export function copilotQuotaProblem(u: CopilotUsage, caps: CopilotCaps, reserve = 0): string | null {
  if (
    u.userRequests >= caps.userRequests ||
    u.userTokens >= caps.userTokens ||
    u.userTokens + reserve > caps.userTokens
  )
    return USER_OVER;
  if (
    u.companyRequests >= caps.companyRequests ||
    u.companyTokens >= caps.companyTokens ||
    u.companyTokens + reserve > caps.companyTokens
  )
    return COMPANY_OVER;
  return null;
}

/** Antes de cada chamada ao modelo: o uso (já com a reserva e o excedente cobrados) passou da cota de tokens? */
export function copilotStepQuotaProblem(u: CopilotUsage, caps: CopilotCaps): string | null {
  if (u.userTokens > caps.userTokens) return USER_OVER;
  if (u.companyTokens > caps.companyTokens) return COMPANY_OVER;
  return null;
}

// ---------- resposta ----------

export const COPILOT_REPLY_MAX = 4000;
const URL_RE = /\b(?:https?:\/\/|www\.)[^\s<>"')\]]+/gi;
/**
 * Domínio "nu" (revisão T1.14): rótulos + TLD de letras, com e-mail, porta, caminho, query e fragmento opcionais
 * (`evil.com/pix?x=1`, `wa.me/55...`, `ana@x.com`). O TLD não pode seguir colado em outra letra ("sim.Não" fica);
 * números como "1.234,56" e "2.0.1" não têm TLD de letras e ficam.
 */
const DOMAIN_RE =
  /(?<![\p{L}\p{N}_@.-])(?:[\p{L}\p{N}._%+-]+@)?(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}(?![\p{L}\p{N}_-])(?::\d{1,5})?(?:[/?#][^\s<>"')\]]*)?/giu;
const MD_LINK_RE = /!?\[([^\]]*)\]\([^)]*\)/g;
// eslint-disable-next-line no-control-regex
const CONTROL_RE = /[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/g;
/** Invisíveis que disfarçam texto/links: largura zero, marcas e isolamentos bidi, BOM, hífen suave. */
const INVISIBLE_RE = /[\u00AD\u061C\u180E\u200B-\u200F\u202A-\u202E\u2060-\u2064\u2066-\u2069\uFEFF]/g;
const removed = (m: string) => `[link removido]${/[.,;:!?]+$/.exec(m)?.[0] ?? ""}`;

/**
 * Limpa a resposta do modelo antes de gravar e mostrar: sem links (URL, domínio nu, e-mail, Markdown de link/imagem),
 * sem caracteres de controle nem invisíveis/bidi e até 4.000 caracteres. O Copiloto não tem motivo para mandar link;
 * um link na resposta só viria de dado injetado (exfiltração ou phishing). Os invisíveis saem PRIMEIRO, para não
 * esconderem um domínio ("evil\u200B.com").
 */
export function sanitizeCopilotReply(text: string): string {
  const clean = text
    .replace(CONTROL_RE, "")
    .replace(INVISIBLE_RE, "")
    .replace(MD_LINK_RE, (_m, t: string) => t)
    .replace(URL_RE, removed)
    .replace(DOMAIN_RE, removed)
    .trim();
  return clean.length > COPILOT_REPLY_MAX ? `${clean.slice(0, COPILOT_REPLY_MAX - 1)}…` : clean;
}

// ---------- WhatsApp ----------

/** DDDs válidos no Brasil (Anatel). */
const DDDS = new Set([
  11, 12, 13, 14, 15, 16, 17, 18, 19, 21, 22, 24, 27, 28, 31, 32, 33, 34, 35, 37, 38, 41, 42, 43, 44, 45, 46,
  47, 48, 49, 51, 53, 54, 55, 61, 62, 63, 64, 65, 66, 67, 68, 69, 71, 73, 74, 75, 77, 79, 81, 82, 83, 84, 85,
  86, 87, 88, 89, 91, 92, 93, 94, 95, 96, 97, 98, 99,
]);

/**
 * Número brasileiro em E.164 (`+55DDNNNNNNNNN`): celular com 9 dígitos começando em 9 ou fixo com 8 começando em 2–5
 * (WhatsApp Business em fixo). Aceita máscara e o +55 opcional; o resto = null.
 */
export function normalizeBrPhone(input: string): string | null {
  if (!/^[\d\s()+.-]+$/.test(input)) return null;
  let d = input.replace(/\D/g, "");
  if (d.length === 12 || d.length === 13) {
    if (!d.startsWith("55")) return null;
    d = d.slice(2);
  }
  if (d.length !== 10 && d.length !== 11) return null;
  const ddd = Number(d.slice(0, 2));
  const local = d.slice(2);
  if (!DDDS.has(ddd)) return null;
  const mobile = local.length === 9 && local.startsWith("9");
  const landline = local.length === 8 && /^[2-5]/.test(local);
  return mobile || landline ? `+55${d}` : null;
}

/**
 * `wa_id`/`from` do WhatsApp (só dígitos) em E.164. Contas brasileiras antigas chegam sem o 9 do celular
 * (55 + DDD + 8 dígitos começando em 6–9): o 9 é recolocado para casar com o número cadastrado.
 */
export function waIdToE164(waId: string): string | null {
  if (!/^\d{8,15}$/.test(waId)) return null;
  if (waId.startsWith("55") && waId.length === 12 && /^[6-9]/.test(waId.slice(4)))
    return `+55${waId.slice(2, 4)}9${waId.slice(4)}`;
  return `+${waId}`;
}

/** Mostra o número com máscara: +55 (11) 98765-4321. */
export function formatBrPhone(e164: string): string {
  const m = /^\+55(\d{2})(\d{4,5})(\d{4})$/.exec(e164);
  return m ? `+55 (${m[1]}) ${m[2]}-${m[3]}` : e164;
}

export const WHATSAPP_CODE_TTL_MIN = 10;
export const WHATSAPP_CODE_MAX_ATTEMPTS = 5;
/** Código de verificação na mensagem: só os 6 dígitos, com "código"/"codigo" opcional na frente. */
export function verificationCodeIn(text: string): string | null {
  const m = /^\s*(?:c[oó]digo\s*:?\s*)?(\d{6})\s*$/i.exec(text);
  return m ? m[1]! : null;
}
